//@flow
import React, {useEffect, useState} from 'react';
import {Grid, makeStyles, Button, Box, IconButton} from '@material-ui/core';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import {Add, Image, Delete, Edit} from '@material-ui/icons';
import {colors} from '../../../../../../assets/styles/colors';
import {getFileUrl} from '../../../../../../utils/image_provider';
import CropModal from '../../../../../../elements/CropModal';
import type {ImageToCrop} from '../../../../../../elements/CropModal';

const TITLE_MAX_LENGTH = 80;
const TEXT_MAX_LENGTH = 200;

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  card: {
    display: 'flex',
    gap: '1rem',
    padding: '1rem',
    borderRadius: '12px',
    border: `1px solid ${colors.grey200}`,
    backgroundColor: '#FAFAFA',
    transition: 'border-color 0.2s ease',

    '&:hover': {
      borderColor: colors.grey300,
    },
  },
  imageContainer: {
    position: 'relative',
    flexShrink: 0,
  },
  imagePlaceholder: {
    width: 80,
    height: 80,
    border: `2px dashed ${colors.grey300}`,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    cursor: 'pointer',
    color: colors.grey500,
    backgroundColor: colors.grey100,
    overflow: 'hidden',
    transition: 'all 0.2s ease',

    '&:hover': {
      backgroundColor: colors.grey200,
      borderColor: colors.primary,
      color: colors.primary,
    },
  },
  previewImage: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  },
  editImageOverlay: {
    position: 'absolute',
    top: '.25rem',
    right: '.25rem',
    backgroundColor: colors.primary,
    color: 'white',
    borderRadius: '50%',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s ease',

    '&:hover': {
      transform: 'scale(1.1)',
    },

    '& svg': {
      fontSize: '14px',
    },
  },
  contentFields: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  fieldWrapper: {
    position: 'relative',
  },
  charCounter: {
    position: 'absolute',
    right: '8px',
    bottom: '8px',
    fontSize: '11px',
    color: colors.grey500,
    pointerEvents: 'none',

    '&[data-warning="true"]': {
      color: colors.warning,
    },
    '&[data-error="true"]': {
      color: colors.error,
    },
  },
  deleteButton: {
    color: colors.grey500,
    padding: '6px',

    '&:hover': {
      color: colors.error,
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  titleInput: {
    flex: 1,
  },
  addButton: {
    border: `2px dashed ${colors.grey300}`,
    borderRadius: '12px',
    width: '100%',
    height: 56,
    textTransform: 'none',
    color: colors.grey600,
    fontWeight: 500,
    transition: 'all 0.2s ease',

    '&:hover': {
      borderColor: colors.primary,
      color: colors.primary,
      backgroundColor: 'rgba(103, 58, 183, 0.04)',
    },
  },
  placeholderContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  placeholderText: {
    fontSize: '10px',
    color: colors.grey500,
  },
}));

type BlockContent = {
  id: string,
  title: string,
  text: string,
  image?: File | null,
  imageId?: string,
};

type Props = {
  initialContent?: BlockContent[],
  onChange?: (contentList: BlockContent[]) => void,
};

export default function InformationBlockContent(props: Props) {
  const {initialContent = [], onChange} = props;
  const classes = useStyles();

  const [contentList, setContentList] = useState<BlockContent[]>(
    initialContent
  );

  // CropModal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<ImageToCrop[] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (initialContent.length > 0) {
      setContentList(initialContent);
    }
  }, [initialContent]);

  const updateContent = (index: number, updated) => {
    const newContentList = [...contentList];
    newContentList[index] = {...newContentList[index], ...updated};
    setContentList(newContentList);
    if (onChange) {
      onChange(newContentList);
    }
  };

  const handleImageSelect = (index: number, file: File | null) => {
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setImageToCrop([{file, preview}]);
    setCurrentImageIndex(index);
    setCropModalOpen(true);
  };

  const handleCropComplete = (croppedImages: ImageToCrop[]) => {
    if (currentImageIndex === null || !croppedImages.length) {
      setCropModalOpen(false);
      return;
    }

    const croppedImage = croppedImages[0];
    updateContent(currentImageIndex, {
      image: croppedImage.file,
      imageId: undefined,
    });

    setCropModalOpen(false);
    setImageToCrop(null);
    setCurrentImageIndex(null);
  };

  const getCharCounterStatus = (current: number, max: number) => {
    const remaining = max - current;
    if (remaining <= 0) return 'error';
    if (remaining <= 10) return 'warning';
    return 'normal';
  };

  const handleAdd = () => {
    const newBenefit: BlockContent = {
      id: Date.now().toString(),
      title: '',
      text: '',
      image: null,
    };
    const updated = [...contentList, newBenefit];
    setContentList(updated);
    if (onChange) {
      onChange(updated);
    }
  };

  const handleDelete = (index: number) => {
    const newContentList = [...contentList];
    newContentList.splice(index, 1);
    setContentList(newContentList);
    if (onChange) {
      onChange(newContentList);
    }
  };

  const getImagePreview = (content: BlockContent) => {
    if (content.image) return URL.createObjectURL(content.image);
    if (content.imageId) return getFileUrl(content.imageId);
    return null;
  };

  return (
    <Box className={classes.wrapper}>
      <Grid container spacing={2}>
        {contentList.map((content, index) => {
          const imagePreview = getImagePreview(content);
          const titleStatus = getCharCounterStatus(
            content.title.length,
            TITLE_MAX_LENGTH
          );
          const textStatus = getCharCounterStatus(
            content.text.length,
            TEXT_MAX_LENGTH
          );

          return (
            <Grid item xs={12} lg={6} key={content.id}>
              <Box className={classes.card}>
                <input
                  type="file"
                  accept="image/*"
                  id={`image-input-${content.id}`}
                  style={{display: 'none'}}
                  onChange={e =>
                    handleImageSelect(index, e.target.files?.[0] || null)
                  }
                />

                <div className={classes.imageContainer}>
                  <label htmlFor={`image-input-${content.id}`}>
                    <Box className={classes.imagePlaceholder}>
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="preview"
                          className={classes.previewImage}
                        />
                      ) : (
                        <div className={classes.placeholderContent}>
                          <Image fontSize="small" />
                          <span className={classes.placeholderText}>
                            1024×1024
                          </span>
                        </div>
                      )}
                    </Box>
                  </label>
                  {imagePreview && (
                    <label
                      htmlFor={`image-input-${content.id}`}
                      className={classes.editImageOverlay}
                    >
                      <Edit />
                    </label>
                  )}
                </div>

                <div className={classes.contentFields}>
                  <div className={classes.headerRow}>
                    <div
                      className={`${classes.fieldWrapper} ${classes.titleInput}`}
                    >
                      <CustomTextField
                        fullWidth
                        size="small"
                        placeholder="Título del contenido"
                        value={content.title}
                        inputProps={{maxLength: TITLE_MAX_LENGTH}}
                        onChange={e =>
                          updateContent(index, {title: e.target.value})
                        }
                      />
                      <span
                        className={classes.charCounter}
                        data-warning={titleStatus === 'warning'}
                        data-error={titleStatus === 'error'}
                      >
                        {content.title.length}/{TITLE_MAX_LENGTH}
                      </span>
                    </div>
                    <IconButton
                      aria-label="Eliminar contenido"
                      onClick={() => handleDelete(index)}
                      className={classes.deleteButton}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>

                  <div className={classes.fieldWrapper}>
                    <CustomTextField
                      fullWidth
                      size="small"
                      placeholder="Descripción del contenido"
                      multiline
                      rows={3}
                      value={content.text}
                      inputProps={{maxLength: TEXT_MAX_LENGTH}}
                      onChange={e =>
                        updateContent(index, {text: e.target.value})
                      }
                    />
                    <span
                      className={classes.charCounter}
                      data-warning={textStatus === 'warning'}
                      data-error={textStatus === 'error'}
                    >
                      {content.text.length}/{TEXT_MAX_LENGTH}
                    </span>
                  </div>
                </div>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Button
        className={classes.addButton}
        startIcon={<Add />}
        onClick={handleAdd}
      >
        Agregar contenido
      </Button>

      {imageToCrop && (
        <CropModal
          open={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false);
            setImageToCrop(null);
            setCurrentImageIndex(null);
          }}
          images={imageToCrop}
          aspect={1}
          onSubmit={handleCropComplete}
          restrictPosition
          compressOptions={{
            maxWidth: 1024,
            maxHeight: 1024,
            quality: 0.9,
          }}
        />
      )}
    </Box>
  );
}
